<template>
  <div>
    <p class="u-heading has-padding-bottom-50 has-border-bottom">
      {{ $t("_.categories") }}
    </p>
    <b-field>
      <b-input
        v-model="searchedValue"
        :placeholder="$t('_placeholder.category_search')"
        :icon-right="searchedValue ? 'close-circle' : null"
        :icon-right-clickable="!!searchedValue"
        expanded
        @icon-right-click="searchedValue = ''"
      />
    </b-field>
    <i18n
      v-if="sellableCategories.length === 0 && searchedValue.length"
      path="_.no_sellable_categories"
    />
    <tree-menu-field
      v-for="category in sellableCategories"
      :key="`category-${category.id}`"
      :category="category"
      :searched-value="searchedValue"
      @click="switchCategory"
    />
  </div>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type {
  IStoreProviderData,
  IStoreProviderMethods
} from "@/models/providers/store";

export default defineComponent({
  name: "CategoriesList",
  inject: {
    $storeProviderData: {
      from: "storeProviderData",
      type: Function as PropType<IStoreProviderData>
    },
    $storeProviderMethods: {
      from: "storeProviderMethods",
      type: Object as PropType<IStoreProviderMethods>
    }
  },
  data: () => ({
    searchedValue: ""
  }),
  computed: {
    $storeData() {
      return this.$storeProviderData();
    },
    sellableCategories() {
      return this.mapCategories(this.$storeData.rootCategories);
    },
    productsAreFiltered() {
      return !!this.$storeData.searchValue.length;
    }
  },
  watch: {
    searchedValue: { handler: "onSearchedValueChange" }
  },
  methods: {
    mapCategories(categories) {
      return categories.length
        ? this.$_.map(
            this.$_.orderBy(
              this.$_.filter(categories, filteredCategory => {
                return this.isFiltered(filteredCategory);
              }),
              "order"
            ),
            category => {
              return this.$_.merge(
                this.$_.pick(category, ["id", "name", "name_translated"]),
                {
                  isOpen:
                    this.searchedValue.length ||
                    this.isOpen(
                      this.$storeProviderMethods.getCategory(
                        this.$storeData.rootCategories,
                        this.$_.get(category, "id")
                      )
                    ),
                  isSearched: this.$_.get(category, "name_translated", "name")
                    .toLowerCase()
                    .includes(this.searchedValue.toLowerCase()),
                  subcategories: this.mapCategories(
                    this.$_.get(category, "subcategories", [])
                  ),
                  products_count:
                    this.$_.get(category, "products_count", 0) +
                    this.$_.get(category, "sub_products_count", 0)
                }
              );
            }
          )
        : [];
    },
    isOpen(category) {
      return (
        category.id === this.$storeData.pathCategoryId ||
        this.$_.some(
          this.$_.get(category, "subcategories", []),
          subcategory => {
            return this.isOpen(subcategory);
          }
        )
      );
    },
    isFiltered(category) {
      if (!this.searchedValue.length) {
        return true;
      }

      return (
        this.$_.get(category, "name_translated", "")
          .toLowerCase()
          .includes(this.searchedValue.toLowerCase()) ||
        this.$_.some(
          this.$_.get(category, "subcategories", []),
          subcategory => {
            return this.isFiltered(subcategory);
          }
        )
      );
    },
    switchCategory(categoryId) {
      if (
        categoryId === this.$storeData.pathCategoryId &&
        this.productsAreFiltered
      ) {
        this.$storeProviderMethods.loadCategoryProducts(
          this.$storeData.pathCategoryId,
          this.$storeData.pathCategoryId
        );
      }

      this.$storeProviderMethods.addCategoryToQuery(categoryId);
      this.searchedValue = "";
    },
    onSearchedValueChange() {
      this.$storeProviderMethods.filterCategories(
        this.searchedValue.length > 0
      );
    }
  }
});
</script>
